import React, { useState, useEffect } from "react";
import {
  getFirestore,
  collection,
  getDocs
} from "firebase/firestore"; // Firestore imports
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import {
  Card,
  Stack,
  Button,
  Container,
  Typography,
  Box,
} from "@mui/material";

import Page from "../components/Page";

export default function Calendar() {
  const [weekendsVisible, setWeekendsVisible] = useState(true);
  const [currentEvents, setCurrentEvents] = useState([]);
  const [events, setEvents] = useState([]); // This will hold both "schedule" + "Pennants"
  const [open, setOpen] = useState(false);

  const db = getFirestore(); // Initialize Firestore

  // Map day names to numbers for FullCalendar
  const dayOfWeekMap = {
    Sunday: 0,
    Monday: 1,
    Tuesday: 2,
    Wednesday: 3,
    Thursday: 4,
    Friday: 5,
    Saturday: 6,
  };

  // Helper: if you need to shift endDate by one day for an inclusive end
  const addOneDay = (dateStr) => {
    // dateStr: "YYYY-MM-DD"
    const dateObj = new Date(dateStr);
    dateObj.setDate(dateObj.getDate() + 1);
    return dateObj.toISOString().split("T")[0];
  };

  // Helper: ensure we have a fallback if endTime is missing
  const ensureValidEndTime = (startTime, endTime) => {
    if (!endTime) {
      // fallback: add 1 hour to startTime
      const [hh, mm] = startTime.split(":");
      let endH = parseInt(hh, 10) + 1;
      const endM = parseInt(mm, 10);
      // wrap around 24 if needed
      if (endH >= 24) endH = 23;
      return `${String(endH).padStart(2, "0")}:${String(endM).padStart(2, "0")}`;
    }
    return endTime;
  };

  // Fetch events from Firestore: both "schedule" and "Pennants"
  useEffect(() => {
    const fetchEvents = async () => {
      try {
        // 1) Fetch "schedule" events
        const eventsCollection = collection(db, "schedule");
        const eventsSnapshot = await getDocs(eventsCollection);
        const scheduleEventsList = eventsSnapshot.docs.map((doc) => {
          const eventData = doc.data();
          // If eventData has an empty daysOfWeek, remove it
          if (eventData.daysOfWeek && eventData.daysOfWeek.length === 0) {
            delete eventData.daysOfWeek;
          }
          return eventData;
        });

        // 2) Fetch "Pennants" so we can show them as recurring events
        const pennantsCollection = collection(db, "Pennants");
        const pennantsSnapshot = await getDocs(pennantsCollection);
        const pennantEventsList = pennantsSnapshot.docs.map((doc) => {
          const p = doc.data();
          // We expect p has: pennantDay, startTime, endTime, startDate, endDate, name
          // Convert day of the week string -> number
          const dayNumber = dayOfWeekMap[p.pennantDay] ?? null;

          // Ensure endTime if missing
          const validEndTime = ensureValidEndTime(p.startTime, p.endTime);

          // Build an event object for FullCalendar recurring
          // If you want the event to repeat *until* p.endDate inclusive,
          // use "endRecur: addOneDay(p.endDate)".
          // If you want exclusive, use "endRecur: p.endDate".
          return {
            id: doc.id,
            title: p.name || "Unnamed Pennant",
            // For recurring events:
            daysOfWeek: dayNumber !== null ? [dayNumber] : [],
            startTime: p.startTime, // "HH:mm"
            endTime: validEndTime,   // "HH:mm"
            startRecur: p.startDate, // "YYYY-MM-DD" 
            endRecur: addOneDay(p.endDate), // inclusive
          };
        });

        // Combine schedule + Pennants
        const combinedEvents = [...scheduleEventsList, ...pennantEventsList];

        setEvents(combinedEvents);
      } catch (error) {
        console.error("Error fetching events or pennants: ", error);
      }
    };

    fetchEvents();
  }, [open]); // re-fetch whenever "open" changes (like your original code)

  // Called after events are initialized or changed in FullCalendar
  const handleEvents = (events) => {
    setCurrentEvents(events);
  };

  const handleEventClick = (clickInfo) => {
    if (
      window.confirm(
        `Are you sure you want to delete the event '${clickInfo.event.title}'?`
      )
    ) {
      clickInfo.event.remove(); // This only removes it from the client-side calendar
      // If you also want to remove it from Firestore, you'd do that here.
    }
  };

  const renderEventContent = (eventInfo) => (
    <>
      <b>{eventInfo.timeText}</b> &nbsp;
      <i>{eventInfo.event.title}</i>
    </>
  );

  return (
    <Page title="Schedule | Ballarat Squash and Racquetball">
      <Container>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          mb={5}
        >
          <Typography variant="h4" gutterBottom>
            Schedule
          </Typography>
        </Stack>

        <div className="demo-app-main">
          <FullCalendar
            plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
            headerToolbar={{
              left: "prev,next today",
              center: "title",
              right: "dayGridMonth,timeGridWeek,timeGridDay",
            }}
            initialView="dayGridMonth"
            editable={false}
            selectable
            selectMirror
            dayMaxEvents
            weekends={weekendsVisible}
            events={events} // All events (schedule + Pennants)
            eventContent={renderEventContent}
            eventClick={handleEventClick}
            eventsSet={handleEvents}
          />

        </div>
      </Container>
    </Page>
  );
}
